
import React from 'react';
import { useNavigate } from 'react-router-dom';

interface Destination {
  title: string;
  duration: string;
  description: string[];
  image: string;
  buttonText: string;
  buttonAction: string;
}

const destinations: Destination[] = [
  {
    title: "Luang Prabang, Laos",
    duration: "Laos - All Inclusive 05 Night / 06 Days",
    description: [
      "UNESCO World Heritage town with golden temples, Mekong sunsets, and serene waterfalls.",
      "Ideal for slow travel & cultural immersion."
    ],
    image: "/images/asia-dest-1.jpg",
    buttonText: "Explore More",
    buttonAction: "explore"
  },
  {
    title: "Around Conoor, The Nilgiris, Tamilnadu, India",
    duration: "06N / 07 Days",
    description: [
      "Charming hill station in the Nilgiris with lush tea estates, misty valleys, and colonial elegance.",
      "Perfect for luxury retreats, nature walks, and serene hillside escapes."
    ],
    image: "/images/asia-dest-2.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Jeju Island and around South Korea",
    duration: "06 Night / 07 Days",
    description: [
      "Volcanic island with lava tubes, waterfalls, and unique Haenyeo (women divers) culture.",
      "A hidden gem beyond Seoul & Busan."
    ],
    image: "/images/asia-dest-3.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Bhutan and Around Specialized with Punakha & Phobjikha Valley",
    duration: "06 Night / 07 Days",
    description: [
      "\"Happiest country in the world\" with breathtaking monasteries & untouched valleys.",
      "Best for spiritual & wellness retreats."
    ],
    image: "/images/asia-dest-4.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Raja Ampat, Indonesia",
    duration: "06 Night / 07 Days",
    description: [
      "Remote paradise of coral reefs, lagoons & limestone karsts.",
      "A diver's dream destination."
    ],
    image: "/images/asia-dest-5.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Sapa, Vietnam",
    duration: "06 Night / 07 Days",
    description: [
      "Terraced rice fields, ethnic minority villages & misty mountains.",
      "Amazing for trekking & cultural immersion."
    ],
    image: "/images/asia-dest-6.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Shirakawa-go, Japan",
    duration: "06 Night / 07 Days",
    description: [
      "Fairytale village with thatched Gassho-style houses in the Japanese Alps.",
      "Magical in winter snow."
    ],
    image: "/images/asia-dest-7.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Mrauk U, Myanmar",
    duration: "06 Night / 07 Days",
    description: [
      "Less-visited ancient city with stone temples and Arakanese history.",
      "A raw alternative to Bagan."
    ],
    image: "/images/asia-dest-8.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Gili Islands, Indonesia (Gili Meno & Gili Air)",
    duration: "06 Night / 07 Days",
    description: [
      "Car-free islands with white beaches & coral reefs.",
      "Niche alternative to Bali & Phuket."
    ],
    image: "/images/asia-dest-9.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  }
];

export const DestinationsSection: React.FC = () => {
  const navigate = useNavigate();

  const handleButtonClick = (action: string, title: string) => {
    if (action === 'enquire') {
      navigate(`/contact?subject=${encodeURIComponent('Enquiry about ' + title)}`);
      return;
    }

    if (action === 'explore') {
      navigate('/group-tours');
      return;
    }
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            All of Asia's Most Popular Country Side
          </h2>
        </div>

        <div className="space-y-16">
          {destinations.map((destination, index) => (
            <div
              key={index}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-8 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                }`}
            >
              <div className={`space-y-6 ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                <h3 className="text-2xl lg:text-3xl font-bold text-gray-900">
                  {destination.title}
                </h3>
                <p className="text-lg text-emerald-600 font-medium">
                  {destination.duration}
                </p>
                <ul className="space-y-3">
                  {destination.description.map((desc, descIndex) => (
                    <li key={descIndex} className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></div>
                      <p className="text-gray-700 leading-relaxed">{desc}</p>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handleButtonClick(destination.buttonAction, destination.title)}
                  className="inline-flex items-center px-6 py-3 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  {destination.buttonText}
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center ml-2"></i>
                </button>
              </div>

              <div className={`${index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}`}>
                <img
                  src={destination.image}
                  alt={destination.title}
                  className="w-full h-80 object-cover rounded-lg shadow-lg"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
